/**
 * Identification data types for card acquisition responses
 *
 * This module provides types and label mappings for loyalty identification data extracted from Adyen terminal
 * responses during card acquisition.
 *
 * @module identification.types
 */

import { IdentificationType } from '@adyen/api-library/lib/src/typings/terminal/identificationType'
import { LoyaltyAccountId } from '@adyen/api-library/lib/src/typings/terminal/loyaltyAccountId'

/** Identification data extracted from a card acquisition response */
export interface IdentificationData {
	/** The loyalty ID extracted from the card/pass */
	loyaltyId: string
	/** How the card was read (Contactless, Mobile, NFC, etc.) */
	entryMode: LoyaltyAccountId.EntryModeEnum[]
	/** Type of identification (AccountNumber, BarCode, Pan, etc.) */
	identificationType: IdentificationType
	/** The loyalty brand/program name */
	loyaltyBrand?: string
}

// Re-export enums for convenience
export { IdentificationType } from '@adyen/api-library/lib/src/typings/terminal/identificationType'
export type EntryModeEnum = LoyaltyAccountId.EntryModeEnum

/** Human-readable labels for entry modes */
export const ENTRY_MODE_LABELS: Partial<Record<LoyaltyAccountId.EntryModeEnum, string>> = {
	[LoyaltyAccountId.EntryModeEnum.Contactless]: 'Contactless',
	[LoyaltyAccountId.EntryModeEnum.Mobile]: 'Mobile Wallet',
	[LoyaltyAccountId.EntryModeEnum.Rfid]: 'RFID',
	[LoyaltyAccountId.EntryModeEnum.Tapped]: 'Tapped',
	[LoyaltyAccountId.EntryModeEnum.File]: 'File',
	[LoyaltyAccountId.EntryModeEnum.Icc]: 'Chip (ICC)',
	[LoyaltyAccountId.EntryModeEnum.Keyed]: 'Keyed',
	[LoyaltyAccountId.EntryModeEnum.MagStripe]: 'Magnetic Stripe',
	[LoyaltyAccountId.EntryModeEnum.Manual]: 'Manual',
	[LoyaltyAccountId.EntryModeEnum.Scanned]: 'Scanned',
	[LoyaltyAccountId.EntryModeEnum.SynchronousIcc]: 'Synchronous ICC',
}

/** Human-readable labels for identification types */
export const IDENTIFICATION_TYPE_LABELS: Partial<Record<IdentificationType, string>> = {
	[IdentificationType.AccountNumber]: 'Account Number',
	[IdentificationType.BarCode]: 'Barcode',
	[IdentificationType.IsoTrack2]: 'ISO Track 2',
	[IdentificationType.Pan]: 'PAN',
	[IdentificationType.PhoneNumber]: 'Phone Number',
}

/** Gets the human-readable label for an entry mode */
export function getEntryModeLabel(mode: LoyaltyAccountId.EntryModeEnum): string {
	return ENTRY_MODE_LABELS[mode] ?? String(mode)
}

/** Gets the human-readable label for an identification type */
export function getIdentificationTypeLabel(type: IdentificationType): string {
	return IDENTIFICATION_TYPE_LABELS[type] ?? String(type)
}

/** Formats entry modes as a comma-separated string */
export function formatEntryModes(modes: LoyaltyAccountId.EntryModeEnum[]): string {
	return modes.map((mode) => getEntryModeLabel(mode)).join(', ')
}
